import { useState } from 'react';
import { Modal } from '../common/Modal';
import { X, Search } from 'lucide-react';
import type { Certificate } from '../../hooks/useCertificates';
import type { Member } from '../../hooks/useMembers';

interface CertificateAccessModalProps {
  certificate: Certificate;
  /** Só quem é "member" entra aqui: dono e administradores não dependem de acesso. */
  members: Member[];
  onClose: () => void;
  onGrant: (userId: string) => Promise<void>;
  onRevoke: (userId: string) => Promise<void>;
  /** Chamado ao terminar de salvar (recarrega a lista). */
  onSaved: () => Promise<void> | void;
}

/** Escolha de membros no mesmo padrão do modal de Grupos (busca + caixas de seleção). */
export function CertificateAccessModal({ certificate, members, onClose, onGrant, onRevoke, onSaved }: CertificateAccessModalProps) {
  const jaTinhamAcesso = certificate.access.map((a) => a.user_id);
  const [selectedIds, setSelectedIds] = useState<string[]>(jaTinhamAcesso);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const term = search.toLowerCase().trim();
  const filtered = members.filter((m) =>
    !term || (m.name || '').toLowerCase().includes(term) || (m.email || '').toLowerCase().includes(term)
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsBusy(true);
    setError(null);
    try {
      // O servidor concede/retira um membro por vez; só o que mudou é enviado.
      for (const id of selectedIds.filter((x) => !jaTinhamAcesso.includes(x))) await onGrant(id);
      for (const id of jaTinhamAcesso.filter((x) => !selectedIds.includes(x))) await onRevoke(id);
      await onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar os acessos.');
      await onSaved();
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal pedirParaFechar={onClose}>
      <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: '520px', padding: 'var(--space-8)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', minWidth: 0, overflowWrap: 'anywhere' }}>Quem pode usar: {certificate.label}</h2>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
            <X size={24} />
          </button>
        </div>

        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5, marginBottom: 'var(--space-4)' }}>
          Donos e administradores não aparecem aqui.
        </p>

        {error && (
          <div style={{ padding: 'var(--space-3)', background: 'var(--color-error-muted)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSave}>
          <h4 style={{ color: 'var(--color-text-primary)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--space-2)' }}>
            Membros ({selectedIds.length} selecionado{selectedIds.length !== 1 ? 's' : ''})
          </h4>

          <div style={{ position: 'relative', marginBottom: 'var(--space-2)' }}>
            <input
              type="text"
              placeholder="Buscar membro por nome ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', padding: 'var(--space-2) var(--space-3) var(--space-2) 32px', background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)', fontSize: 'var(--text-xs)', outline: 'none' }}
            />
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)', pointerEvents: 'none' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: '240px', overflowY: 'auto', background: 'var(--color-bg-root)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-default)', marginBottom: 'var(--space-6)' }}>
            {filtered.length === 0 ? (
              <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)', textAlign: 'center', padding: 'var(--space-3)' }}>
                {search ? 'Nenhum membro encontrado.' : 'Nenhum membro cadastrado.'}
              </div>
            ) : (
              filtered.map((member) => (
                <label key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedIds.includes(member.id)} onChange={() => toggle(member.id)} />
                  <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', minWidth: 0, overflowWrap: 'anywhere' }}>{member.name || member.email}</span>
                </label>
              ))
            )}
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 'var(--space-3)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}>Cancelar</button>
            <button type="submit" disabled={isBusy} style={{ flex: 1, padding: 'var(--space-3)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', borderRadius: 'var(--radius-md)', cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.6 : 1 }}>
              {isBusy ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
